import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaLocationDot } from "react-icons/fa6";
import { Pencil, RotateCcw } from "lucide-react";
import { ColorPicker, ColorService } from "react-color-palette";
import "react-color-palette/css";
import { toast } from "../../utils/toast";
import { fetchMarkerColors, SaveColors } from "../../service/setting";
import ModalCloseButton from "../common/ModalCloseButton";
import translations from "../common/Translations";

const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;
const isValidHexColor = (value) => typeof value === "string" && HEX_COLOR_PATTERN.test(value.trim());

const COLOR_ARRAY = [
  "#4379F2",
  "#F3C623",
  "#FF3333",
  "#CC2B52",
  "#6EC207",
  "#CD5C08",
  "#7A1CAC",
  "#E85C0D",
  "#939185",
  "#124076",
];

const CustomMarkerSettingsModal = ({
  isOpen,
  onClose,
  workflowId,
  langMode,
  theme,
  onSaveSuccess,
}) => {
  const statusList = useMemo(
    () => [
      { name: translations[langMode].active, taskStatus: "Active" },
      { name: translations[langMode].assigned, taskStatus: "Assigned" },
      { name: translations[langMode].onhold, taskStatus: "On_Hold" },
      { name: translations[langMode].progress, taskStatus: "In_Progress" },
      { name: translations[langMode].completed, taskStatus: "Completed" },
    ],
    [langMode]
  );

  const defaultColorMap = useMemo(
    () =>
      statusList.reduce((map, status, index) => {
        map[status.taskStatus] = COLOR_ARRAY[index % COLOR_ARRAY.length];
        return map;
      }, {}),
    [statusList]
  );

  const toastShownRef = useRef({ workflowToast: false, colorToast: false });
  const [selectedColor, setSelectedColor] = useState([]);
  const [hasColors, setHasColors] = useState(false);
  const [colorData, setColorData] = useState([]);
  const isRtl = langMode === "ar" || langMode === "ur";

  // Which row's color picker popover is open (only one at a time), and the
  // full IColor object (hex/rgb/hsv) react-color-palette's <ColorPicker />
  // needs for that row's current color.
  const [openPickerIndex, setOpenPickerIndex] = useState(null);
  const [activeColor, setActiveColor] = useState(null);
  const [pickerOpensUpward, setPickerOpensUpward] = useState(false);

  // Anchor this panel beside the "custom-marker" pin button in the map's
  // icon rail, the same way DrawControl's draw-tool flyout anchors itself
  // beside the "edit" button (see MapView.jsx's syncDrawControlPosition) -
  // measured live instead of centering as a full-screen modal.
  const panelRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0, ready: false });

  useEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      const panel = panelRef.current;
      const mapEl = document.getElementById("map");
      const triggerBtn = mapEl?.querySelector('[data-map-toolbar-button-id="custom-marker"]');
      if (!panel || !mapEl || !triggerBtn) return;

      const mapRect = mapEl.getBoundingClientRect();
      const triggerRect = triggerBtn.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const gap = 10;
      const verticalPadding = 8;
      const horizontalPadding = 8;

      const minTop = mapRect.top + verticalPadding;
      const maxTop = mapRect.bottom - panelRect.height - verticalPadding;
      const centeredTop = triggerRect.top + (triggerRect.height - panelRect.height) / 2;
      const top = Math.max(minTop, Math.min(maxTop, centeredTop));

      const minLeft = mapRect.left + horizontalPadding;
      const maxLeft = mapRect.right - panelRect.width - horizontalPadding;
      const preferredLeftLtr = triggerRect.left - panelRect.width - gap;
      const alternateLeftLtr = triggerRect.right + gap;
      const preferredLeftRtl = triggerRect.right + gap;
      const alternateLeftRtl = triggerRect.left - panelRect.width - gap;
      const clampLeft = (value) => Math.max(minLeft, Math.min(maxLeft, value));
      const canFit = (value) => value >= minLeft && value <= maxLeft;

      let left;
      if (isRtl) {
        left = canFit(preferredLeftRtl)
          ? preferredLeftRtl
          : canFit(alternateLeftRtl)
            ? alternateLeftRtl
            : clampLeft(preferredLeftRtl);
      } else {
        left = canFit(preferredLeftLtr)
          ? preferredLeftLtr
          : canFit(alternateLeftLtr)
            ? alternateLeftLtr
            : clampLeft(preferredLeftLtr);
      }

      setPanelPosition({ top: Math.round(top), left: Math.round(left), ready: true });
    };

    updatePosition();
    const timeoutIds = [
      window.setTimeout(updatePosition, 0),
      window.setTimeout(updatePosition, 120),
      window.setTimeout(updatePosition, 240),
      window.setTimeout(updatePosition, 420),
    ];
    window.addEventListener("resize", updatePosition);

    const mapEl = document.getElementById("map");
    let resizeObserver = null;
    if (mapEl && typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updatePosition);
      resizeObserver.observe(mapEl);
      const triggerBtn = mapEl.querySelector('[data-map-toolbar-button-id="custom-marker"]');
      if (triggerBtn) resizeObserver.observe(triggerBtn);
    }

    return () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      window.removeEventListener("resize", updatePosition);
      if (resizeObserver) resizeObserver.disconnect();
      setPanelPosition({ top: 0, left: 0, ready: false });
    };
  }, [isOpen, isRtl]);

  useEffect(() => {
    if (!isOpen) {
      setOpenPickerIndex(null);
      setActiveColor(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (!workflowId) {
      if (!toastShownRef.current.workflowToast) {
        toast.warn(translations[langMode].selectWorkflowFirst);
        toastShownRef.current.workflowToast = true;
      }
      return;
    }

    const fetchColors = async () => {
      try {
        const fetchedColorData = await fetchMarkerColors(workflowId);
        setColorData(fetchedColorData);
        setHasColors(fetchedColorData.length > 0);

        if (fetchedColorData.length === 0) {
          if (!toastShownRef.current.colorToast) {
            toast.warn(translations[langMode].thecustomcodewarningmessage);
            toastShownRef.current.colorToast = true;
          }
        }

        const mappedColors = statusList.map((status) => {
          const colorEntry = fetchedColorData.find(
            (color) => color.taskStatus === status.taskStatus
          );
          return colorEntry
            ? colorEntry.color
            : defaultColorMap[status.taskStatus];
        });

        setSelectedColor(mappedColors);
      } catch (error) {
        console.error("Error fetching colors:", error);
        setSelectedColor(
          statusList.map((status) => defaultColorMap[status.taskStatus])
        );
      }
    };

    fetchColors();
  }, [isOpen, workflowId, langMode, statusList, defaultColorMap]);

  const handleColorChange = (statusIndex, color) => {
    const updatedColors = [...selectedColor];
    updatedColors[statusIndex] = color;
    setSelectedColor(updatedColors);
  };

  const handleReset = () => {
    setSelectedColor(statusList.map((status) => defaultColorMap[status.taskStatus]));
    setOpenPickerIndex(null);
    setActiveColor(null);
  };

  const handleSave = async () => {
    if (!workflowId) {
      toast.warn("Please select a workflow first.");
      return;
    }

    const markerColorList = statusList.map((status, index) => {
      const colorEntry = colorData.find(
        (color) => color.taskStatus === status.taskStatus
      );

      return {
        id: colorEntry ? colorEntry.id : undefined,
        taskStatus: status.taskStatus,
        color: selectedColor[index] || defaultColorMap[status.taskStatus],
      };
    });

    const payload = {
      workflowId,
      markerColorList,
    };

    try {
      await SaveColors(payload);
      toast.success(translations[langMode].makerColor);
      onSaveSuccess?.({ workflowId, markerColorList });
      onClose?.();
    } catch (error) {
      console.error("Error saving marker colors:", error);
      toast.error(translations[langMode].failedtoSaveMakerColor);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={panelRef}
      className={`fixed z-[1200] w-[min(92vw,26rem)] rounded-xl border bg-white p-5 shadow-xl transition-opacity duration-150 ${
        theme === "dark" ? "dark-mode" : ""
      }`}
      style={{
        top: `${panelPosition.top}px`,
        left: `${panelPosition.left}px`,
        opacity: panelPosition.ready ? 1 : 0,
        pointerEvents: panelPosition.ready ? "auto" : "none",
      }}
    >
      <div className="mb-5 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {translations[langMode].custommarker}
        </h3>
        <ModalCloseButton onClick={onClose} ariaLabel="Close custom marker modal" />
      </div>

      <div className="flex flex-col gap-3">
        {statusList.map((status, statusIndex) => {
          const currentColor =
            selectedColor[statusIndex] || defaultColorMap[status.taskStatus];
          const isPickerOpen = openPickerIndex === statusIndex;

          return (
            <div key={status.taskStatus} className="relative flex items-center gap-3">
              <FaLocationDot size={28} style={{ color: currentColor }} className="shrink-0" />
              <span
                className={`w-24 shrink-0 text-sm font-medium ${
                  theme === "dark"
                    ? "text-gray-300"
                    : theme === "high-contrast"
                    ? "text-black font-bold"
                    : "text-gray-700"
                }`}
              >
                {status.name}
              </span>
              <input
                type="text"
                value={currentColor}
                onChange={(e) => handleColorChange(statusIndex, e.target.value)}
                placeholder="#ffffff"
                className={`h-9 min-w-0 flex-1 rounded-lg border px-3 text-sm ${
                  theme === "dark"
                    ? "border-gray-700 bg-gray-800 text-white"
                    : "border-gray-300 bg-white text-gray-900"
                }`}
              />
              <button
                type="button"
                onClick={(e) => {
                  if (isPickerOpen) {
                    setOpenPickerIndex(null);
                    setActiveColor(null);
                    return;
                  }
                  const seedColor = isValidHexColor(currentColor)
                    ? currentColor
                    : defaultColorMap[status.taskStatus];
                  setActiveColor(ColorService.convert("hex", seedColor));
                  setOpenPickerIndex(statusIndex);

                  // Open upward instead of downward when there isn't enough
                  // room below the button (popover is ~300px tall), so it
                  // never gets clamped off the bottom of the map/viewport.
                  const btnRect = e.currentTarget.getBoundingClientRect();
                  const estimatedPopoverHeight = 300;
                  setPickerOpensUpward(
                    window.innerHeight - btnRect.bottom < estimatedPopoverHeight &&
                      btnRect.top > estimatedPopoverHeight
                  );
                }}
                aria-label={`Pick color for ${status.name}`}
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                  isPickerOpen
                    ? "border-[#236DB4] text-[#236DB4] bg-blue-50"
                    : theme === "dark"
                    ? "border-gray-700 text-gray-300 hover:bg-gray-800"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                <Pencil size={16} />
              </button>

              {isPickerOpen && activeColor && (
                <div
                  className={`custom-marker-color-popover absolute right-0 z-30 w-56 rounded-xl border p-2.5 shadow-xl ${
                    pickerOpensUpward ? "bottom-full mb-2" : "top-full mt-2"
                  } ${
                    theme === "dark"
                      ? "border-gray-700 bg-gray-800"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <ColorPicker
                    height={110}
                    color={activeColor}
                    hideAlpha
                    hideInput={["rgb", "hsv"]}
                    onChange={(color) => {
                      setActiveColor(color);
                      handleColorChange(statusIndex, color.hex);
                    }}
                  />
                  <div className="mt-2.5 flex flex-wrap gap-1.5 border-t border-gray-100 pt-2.5">
                    {COLOR_ARRAY.map((swatch) => (
                      <button
                        key={swatch}
                        type="button"
                        onClick={() => {
                          setActiveColor(ColorService.convert("hex", swatch));
                          handleColorChange(statusIndex, swatch);
                        }}
                        aria-label={`Use color ${swatch}`}
                        className={`h-5 w-5 rounded-full ring-2 ring-offset-1 transition-transform hover:scale-110 ${
                          currentColor?.toLowerCase() === swatch.toLowerCase()
                            ? "ring-gray-900"
                            : "ring-transparent"
                        }`}
                        style={{ backgroundColor: swatch }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex gap-3 border-t border-gray-200 pt-5">
        <button data-button-intent="warning"
          type="button"
          onClick={handleReset}
          className="flex h-10 flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#FF0000] text-sm font-medium text-white transition-colors hover:bg-[#e60000]"
        >
          <RotateCcw size={15} />
          {translations[langMode]?.reset ?? "Reset"}
        </button>
        <button data-button-intent="success"
          type="button"
          onClick={handleSave}
          className="flex h-10 flex-1 items-center justify-center rounded-xl bg-[#236DB4] text-sm font-medium text-white transition-colors hover:bg-[#1d5c99]"
        >
          {hasColors ? translations[langMode].update : translations[langMode].save}
        </button>
      </div>
    </div>
  );
};

export default CustomMarkerSettingsModal;
